/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/creditInvoiceData.scss';

import {
    ChangeKeyFunction, DVRDNumberInput, ElementPosition, ErrorType, Loader, parseMoney, voidFunction
} from '@dvrd/dvr-controls';
import React, {FocusEventHandler, Fragment, MouseEventHandler} from 'react';
import Invoice from "../../../models/invoiceModel";
import {BolmateButton} from "../../controls";
import InvoiceData from "../data/invoiceData";
import {CreditItem} from "../../../models/orderItemModel";
import classNames from 'classnames';
import {EditMode} from "../../../util/interfaces";
import {translateVatValue} from "../../../util/vatUtil";

interface Props {
    onChangeField: ChangeKeyFunction;
    onChangePriceIncl: ChangeKeyFunction;
    onChangePercentage: ChangeKeyFunction;
    onChangeQuantity: ChangeKeyFunction;
    onSubmit: MouseEventHandler;
    onClose: MouseEventHandler;
    onResetError: (id: string) => FocusEventHandler;
    items: CreditItem[];
    invoice: Invoice;
    loading: boolean;
    errors: Record<string, ErrorType>;
    mode: EditMode;
}

export default function CreditInvoiceData(props: Props) {
    const {
        invoice, loading, onSubmit, items, onChangePercentage, onChangePriceIncl, onChangeQuantity, onClose, onResetError,
        errors, mode, onChangeField
    } = props;

    function renderItems() {
        return (
            <div className='items-container'>
                <div className='items-header'>
                    <label className='col-title'>Te Crediteren Productregels</label>
                </div>
                <div className='order-items'>
                    <Loader active={loading}/>
                    <div className='row head'>
                        <label>EAN</label>
                        <label>NAAM</label>
                        <label>PRIJS INCL. BTW</label>
                        <label>AANTAL</label>
                        <label>BTW</label>
                        <label>SUBTOTAAL INCL. BTW</label>
                    </div>
                    {items.map(renderItem)}
                </div>
            </div>
        )
    }

    function renderItem(item: CreditItem) {
        const {orderItem} = item;
        if (!orderItem) return null;
        const error = errors[orderItem.id];
        return (
            <Fragment key={orderItem.id}>
                <div className='row order'>
                    <label>{orderItem.ean}</label>
                    <label>{orderItem.title}</label>
                    <label>&euro; {parseMoney(orderItem.piecePrice)}</label>
                    <label>{orderItem.quantity}</label>
                    <label className='vat-label'>{translateVatValue(orderItem.vat)}</label>
                    <label>&euro; {parseMoney(orderItem.price)}</label>
                </div>
                <div className='row'>
                    <label/>
                    <label className={classNames('error', error && 'visible')}>{error}</label>
                    <div className='price-container'>
                        <DVRDNumberInput onChange={onChangePriceIncl(orderItem.id)} value={item.priceIncl}
                                         label='Prijs' onFocus={onResetError(orderItem.id)}
                                         ornaments={{
                                             element: <span>- &euro;</span>,
                                             placement: ElementPosition.LEFT
                                         }} asCurrency/>
                        <DVRDNumberInput onChange={onChangePercentage(orderItem.id)} value={item.percentage}
                                         label='Percentage' onFocus={onResetError(orderItem.id)} ornaments={[{
                            element: <span>-</span>,
                            placement: ElementPosition.LEFT
                        }, {element: <span>%</span>, placement: ElementPosition.RIGHT}]}/>
                    </div>
                    <DVRDNumberInput onChange={onChangeQuantity(orderItem.id)} value={item.quantity}
                                     label='Aantal' onFocus={onResetError(orderItem.id)}/>
                    <DVRDNumberInput onChange={voidFunction} value={item.vatAmount} label='BTW' disabled ornaments={{
                        element: <span>- &euro;</span>,
                        placement: ElementPosition.LEFT
                    }} asCurrency/>
                    <label>&euro; {parseMoney(item.subTotalIncl)}</label>
                </div>
            </Fragment>
        )
    }

    const saveText = mode === EditMode.EDIT ? 'Opslaan' : 'Aanmaken';
    return (
        <div className='credit-invoice-data'>
            <div className='header-container'>
                <BolmateButton onClick={onClose} label='Annuleren' disabled={loading} secondary/>
                <BolmateButton onClick={onSubmit} label={`${saveText} en naar creditfactuur`} disabled={loading}/>
            </div>
            <InvoiceData invoice={invoice} onChange={onChangeField} disabled/>
            {renderItems()}
        </div>
    )
}